import React, { useEffect, useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import './NotificationList.css';

interface NotificationListProps {
  isVisible: boolean;
  onClose: () => void;
}

const NotificationList: React.FC<NotificationListProps> = ({ isVisible, onClose }) => {
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearNotifications, loading } = useNotification();
  const [activeTab, setActiveTab] = useState<'all' | 'unread'>('all');

  const filteredNotifications = activeTab === 'unread' 
    ? notifications.filter(n => !n.isRead)
    : notifications;

  const handleMarkAsRead = async (notificationId: string) => {
    if (!notificationId) {
      console.error('Notification ID is undefined');
      return;
    }
    await markAsRead(notificationId);
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead();
  };

  const handleClearRead = async () => {
    await clearNotifications();
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'new_order': return '🛒';
      case 'payment_success': return '💳';
      case 'merged_payment_success': return '🔄';
      case 'table_reservation': return '🪑';
      case 'guest_order': return '📱';
      case 'order_cancelled': return '❌';
      case 'order_updated': return '📝';
      default: return '🔔';
    }
  };

  const formatTime = (timestamp: string | Date) => {
    try {
      const date = new Date(timestamp);
      
      // Kiểm tra xem date có hợp lệ không
      if (isNaN(date.getTime())) {
        return 'Vừa xong';
      }
      
      const now = new Date();
      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
      
      if (diffInMinutes < 1) return 'Vừa xong';
      if (diffInMinutes < 60) return `${diffInMinutes} phút trước`;
      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} giờ trước`;
      return date.toLocaleDateString('vi-VN');
    } catch (error) {
      console.error('Error formatting time:', error, 'timestamp:', timestamp);
      return 'Vừa xong';
    }
  };

  if (!isVisible) return null;

  return (
    <div className="notification-list-overlay" onClick={onClose}>
      <div className="notification-list-container" onClick={(e) => e.stopPropagation()}>
        <div className="notification-list-header">
          <h3>Thông báo ({unreadCount} chưa đọc)</h3>
          <button className="notification-list-close" onClick={onClose}>×</button>
        </div>

        <div className="notification-list-tabs">
          <button 
            className={`notification-tab ${activeTab === 'all' ? 'active' : ''}`}
            onClick={() => setActiveTab('all')}
          >
            Tất cả ({notifications.length})
          </button>
          <button 
            className={`notification-tab ${activeTab === 'unread' ? 'active' : ''}`}
            onClick={() => setActiveTab('unread')}
          >
            Chưa đọc ({unreadCount})
          </button>
        </div>

        <div className="notification-list-actions">
          <button 
            className="notification-action-btn"
            onClick={handleMarkAllAsRead}
            disabled={unreadCount === 0}
          >
            Đánh dấu tất cả đã đọc
          </button>
          <button 
            className="notification-action-btn"
            onClick={handleClearRead}
            disabled={notifications.filter(n => n.isRead).length === 0}
          >
            Xóa đã đọc
          </button>
        </div>

        <div className="notification-list-content">
          {loading ? (
            <div className="notification-loading">Đang tải...</div>
          ) : filteredNotifications.length === 0 ? (
            <div className="notification-empty">
              {activeTab === 'unread' ? 'Không có thông báo chưa đọc' : 'Không có thông báo nào'}
            </div>
          ) : (
            <div className="notification-items">
              {filteredNotifications.map((notification) => (
                <div 
                  key={notification._id} 
                  className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                  onClick={() => {
                    if (!notification.isRead && notification._id) {
                      handleMarkAsRead(notification._id);
                    }
                  }}
                >
                  <div className="notification-icon">
                    {getNotificationIcon(notification.type)}
                  </div>
                  <div className="notification-content">
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">
                      {formatTime(notification.timestamp)}
                    </div>
                    {notification.customerName && (
                      <div className="notification-details">
                        Khách: {notification.customerName}
                      </div>
                    )}
                    {notification.tableName && (
                      <div className="notification-details">
                        Bàn: {notification.tableName}
                      </div>
                    )}
                    {notification.total && (
                      <div className="notification-details">
                        Tổng: {typeof notification.total === 'number' ? notification.total.toLocaleString('vi-VN') : notification.total}đ
                      </div>
                    )}
                  </div>
                  {!notification.isRead && (
                    <div className="notification-unread-indicator"></div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationList; 